import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';


@Injectable({
    providedIn: 'root'
})
export class AegisIntegrationService {

    private baseUrl = environment.backendurl;

    constructor(private http: HttpClient) { }

    private aegisCandidateData: Subject<any> = new Subject();

    get getAegisTagCandidateEvent(): Observable<any> {
        return this.aegisCandidateData.asObservable();
    };

    emitAegisTagCandidateEvent(candidateData: any) {
        this.aegisCandidateData.next(candidateData);
    };

    /**
     * Create a new Aegis integration.
     * @param accessCode The access code for the Aegis integration.
     * @param state The state parameter for the Aegis integration.
     * @returns An observable of the HTTP response containing the status, data, and optional error.
     */
    createAegisIntegration(accessCode: string, state: string): Observable<HttpResponse<{ status: string, data: any, error?: string }>> {
        return this.http.post<{ status: string, data: any, error?: string }>(`${this.baseUrl}/external/connectaegis`, { access_code: accessCode, state }, { observe: 'response' });
    };


    /**
     * Check if an Aegis integration is connected.
     * @returns An observable of the HTTP response containing the status, data, and optional error.
     */
    checkAegisIntegrationConnected(): Observable<HttpResponse<{ status: string, data: any, error?: string }>> {
        return this.http.get<{ status: string, data: any, error?: string }>(`${this.baseUrl}/external/aegisintegration`, { observe: 'response' });
    };


    /**
     * Disconnect an Aegis integration.
     * @param connection_id The ID of the Aegis integration connection to disconnect.
     * @returns An observable of the HTTP response containing the status, data, and optional error.
     */
    disconnectAegisIntegration(connection_id: number): Observable<HttpResponse<{ status: string, data: any, error?: string }>> {
        return this.http.post<{ status: string, data: any, error?: string }>(`${this.baseUrl}/external/disconnectaegis`, { connection_id }, { observe: 'response' });
    };

    /**
     * Check if a candidate exists in Aegis.
     * @param candidate The candidate data to check.
     * @returns An observable of the HTTP response containing the status, candidate, and optional error.
     */
    checkCandidateExistInAegis(candidate: any): Observable<HttpResponse<{ status: string, candidate: any, error?: string }>> {
        return this.http.post<{ status: string, candidate: any, error?: string }>(`${this.baseUrl}/external/checkcandidateexistinaegis`, { candidateData: candidate }, { observe: 'response' });
    };  

    /**
     * Get the list of Aegis mandates.
     * @param searchname The name to search for in the Aegis mandate list.
     * @param start The starting index for pagination.
     * @param limit The maximum number of results to return.
     * @returns An observable of the HTTP response containing the status, data, and optional error.
     */
    getAegisMandateList(searchname: string | null, start: number, limit: number): Observable<HttpResponse<{ status: string, data: any, error?: string }>> {

        let params = new HttpParams();
        if (searchname && searchname.trim() && searchname.trim().length > 0) {
            params = params.append('name', searchname);
        }
        params = params.append('start', start.toString());
        params = params.append('limit', limit.toString());

        const url = `${this.baseUrl}/external/aegismandatelist`;

        return this.http.get<{ status: string, data: any, error?: string }>(url, { params: params, observe: 'response' });
    };

    /**
     * Tag a candidate to a mandate.
     * @param candidate The candidate(s) to tag.
     * @param mandateId The ID of the mandate to tag the candidate(s) to.
     * @returns An observable of the HTTP response containing the status, data, and optional error.
     */
    tagCandidateToMandate(data: any, mandateId: number): Observable<HttpResponse<{ status: string, data: any, error?: string }>> {
        const url = `${this.baseUrl}/external/mandate/${mandateId}/tagCandidate`;
        return this.http.post<{ status: string, data: any, error?: string }>(url, data, { observe: 'response' });
    };

};